<template>
  <v-container fluid class="pa-0 login-container">
    <v-overlay :value="isLoading" absolute>
      <div class="d-flex flex-column align-center justify-center">
        <v-progress-circular indeterminate size="64" :width="5" />
        <span class="my-4">กำลังโหลดข้อมูล</span>
      </div>
    </v-overlay>

    <RegisterRiderWelcome
      v-if="stage === 'welcome'"
      @start="goInfo"
      @back="handleBackFromWelcome"
    />

    <div v-else-if="stage === 'info'" class="stage-wrapper">
      <StepperRider :current-step="1" @step-click="jumpToStep" />
      <RegisterRiderInfo
        :value="infoForm"
        @next="handleInfoNext"
        @back="stage = 'welcome'"
      />
    </div>

    <div v-else-if="stage === 'iden'" class="stage-wrapper">
      <StepperRider :current-step="2" @step-click="jumpToStep" />
      <RegisterRiderIdenCard
        :value="idenForm"
        @input="idenForm = { ...idenForm, ...$event }"
        @back="stage = 'info'"
        @next="handleIdenNext"
      />
    </div>

    <div v-else-if="stage === 'vehicle'" class="stage-wrapper">
      <StepperRider :current-step="3" @step-click="jumpToStep" />
      <RegisterRiderVehicle
        :value="vehicleForm"
        @input="vehicleForm = { ...vehicleForm, ...$event }"
        @back="stage = 'iden'"
        @next="handleVehicleNext"
      />
    </div>

    <div v-else-if="stage === 'send'" class="stage-wrapper">
      <StepperRider :current-step="4" @step-click="jumpToStep" />
      <RegisterRiderSend @back="stage = 'vehicle'" @submit="handleSubmit" />
    </div>
  </v-container>
</template>

<script>
import StepperRider from "@/components/RegisterRider/StepperRider.vue";
import RegisterRiderWelcome from "@/components/RegisterRider/RegisterRiderWelcome.vue";
import RegisterRiderInfo from "@/components/RegisterRider/RegisterRiderInfo.vue";
import RegisterRiderIdenCard from "@/components/RegisterRider/RegisterRiderIdenCard.vue";
import RegisterRiderVehicle from "@/components/RegisterRider/RegisterRiderVehicle.vue";
import RegisterRiderSend from "@/components/RegisterRider/RegisterRiderSend.vue";

import { riderRegisterApi } from "@/services/api/auth";
import swalAlert from "@/services/alert/swalServices";

export default {
  name: "RegisterRider",
  components: {
    StepperRider,
    RegisterRiderWelcome,
    RegisterRiderInfo,
    RegisterRiderIdenCard,
    RegisterRiderVehicle,
    RegisterRiderSend,
  },
  data() {
    return {
      isLoading: false,
      stage: "welcome",
      infoForm: {},
      idenForm: {},
      vehicleForm: {},
    };
  },
  methods: {
    handleBackFromWelcome() {
      this.$router.push({ name: "login_rider" });
    },
    jumpToStep(step) {
      const map = { 1: "info", 2: "iden", 3: "vehicle", 4: "send" };
      if (map[step]) this.stage = map[step];
    },
    goInfo() {
      this.stage = "info";
    },
    handleInfoNext(payload) {
      this.infoForm = payload;
      this.stage = "iden";
    },
    handleIdenNext(payload) {
      this.idenForm = payload;
      this.stage = "vehicle";
    },
    handleVehicleNext(payload) {
      this.vehicleForm = payload;
      this.stage = "send";
    },

    toISODate(d) {
      if (!d) return "";
      const x = new Date(d);
      if (isNaN(x)) return "";
      const yyyy = x.getFullYear();
      const mm = String(x.getMonth() + 1).padStart(2, "0");
      const dd = String(x.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    },

    async handleSubmit() {
      try {
        this.isLoading = true;

        const i = this.infoForm || {};
        const k = this.idenForm || {};
        const v = this.vehicleForm || {};

        if (i.password !== i.password2) {
          await swalAlert.Error("รหัสผ่านไม่ตรงกัน");
          return;
        }

        // birthDate จาก RegisterRiderIdenCard (day, monthNameTH, year)
        const birthDate = (() => {
          const { day, month, year } = k.birth || {};
          if (!day || !month || !year) return "";
          const months = [
            "มกราคม",
            "กุมภาพันธ์",
            "มีนาคม",
            "เมษายน",
            "พฤษภาคม",
            "มิถุนายน",
            "กรกฎาคม",
            "สิงหาคม",
            "กันยายน",
            "ตุลาคม",
            "พฤศจิกายน",
            "ธันวาคม",
          ];
          const idx = months.indexOf(month);
          if (idx < 0) return "";
          const mm = String(idx + 1).padStart(2, "0");
          const dd = String(day).padStart(2, "0");
          return `${year}-${mm}-${dd}`;
        })();

        // สร้าง FormData สำหรับส่งไฟล์
        const formData = new FormData();
        
        // ข้อมูลพื้นฐาน
        formData.append("username", (i.username || "").trim());
        formData.append("province", i.province || "");
        formData.append("subdistrictId", i.subdistrictId || "");
        formData.append("addressDetail", i.addressDetail || "");
        formData.append("phone", i.phone || "");
        formData.append("email", (i.email || "").trim());
        formData.append("password", i.password || "");
        formData.append("bankAccountNumber", i.accountNumber || "");
        formData.append("bankName", i.bankName || "");
        formData.append("inviteCode", (i.refCode || "").toUpperCase());

        // ข้อมูลส่วนตัว
        formData.append("personalInfo[nationalId]", k.citizenId || "");
        formData.append("personalInfo[prefix]", k.prefix || "");
        formData.append("personalInfo[firstName]", k.firstName || "");
        formData.append("personalInfo[lastName]", k.lastName || "");
        formData.append("personalInfo[birthDate]", birthDate);
        formData.append("personalInfo[idIssueDate]", this.toISODate(k.issueDate) || "");
        formData.append("personalInfo[idExpireDate]", this.toISODate(k.expireDate) || "");

        // ข้อมูลรถ
        const isBicycle = /bicycle|จักรยาน(?!ยนต์)/i.test(v.vehicleType || "");
        formData.append("vehicleType", v.vehicleType || "");
        formData.append("vehicleTypeId", v.vehicleTypeId || "");
        formData.append("vehicleBrand", v.vehicleBrand || "");
        formData.append("vehicleModel", v.vehicleModel || "");
        formData.append("vehicleColor", v.vehicleColor || "");
        // ส่งเลขทะเบียนรถเฉพาะเมื่อไม่ใช่จักรยาน
        if (v.vehicleType && !isBicycle) {
          formData.append("licensePlate", v.licensePlate || "");
        }
        // ไฟล์เอกสาร
        // รูปยานพาหนะคู่คนขับ (ทุกประเภท)
        if (v.vehicleWithDriverFile) {
          formData.append("vehicleWithDriverPhoto", v.vehicleWithDriverFile);
        }

        // สำหรับจักรยาน: ส่งบัตรประชาชน
        if (isBicycle && v.idCardFile) {
          formData.append("idCardDocument", v.idCardFile);
        }

        // สำหรับรถยนต์/มอไซต์: ส่งเอกสารรถและใบขับขี่
        if (v.vehicleType && !isBicycle) {
          if (v.vehicleDocFile) {
            formData.append("vehicleDocument", v.vehicleDocFile);
          }
          if (v.driverLicenseFile) {
            formData.append("driverLicense", v.driverLicenseFile);
          }
          if (v.passengerLicenseFile) {
            formData.append("passengerLicense", v.passengerLicenseFile);
          }
          if (v.ownerConsentFile) {
            formData.append("ownerConsentLetter", v.ownerConsentFile);
          }
        }

        await riderRegisterApi(formData);

        await swalAlert.Success("ลงทะเบียนไรเดอร์สำเร็จ");
        this.$router.push({ name: "login_rider" });
      } catch (err) {
        const s = err?.response?.status;
        const msg = err?.response?.data?.message || "เกิดข้อผิดพลาด";
        if (s === 400)
          await swalAlert.Error(msg || "กรุณากรอกข้อมูลให้ครบถ้วน");
        else if (s === 401) await swalAlert.Error("กรุณาเข้าสู่ระบบก่อน");
        else if (s === 409) await swalAlert.Error(msg || "อีเมลหรือชื่อผู้ใช้นี้ถูกใช้งานแล้ว");
        else await swalAlert.Error(`ส่งแบบฟอร์มไม่สำเร็จ: ${msg}`);
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.login-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin: 0;
  padding: 0;
  min-height: 100vh;
}
.stage-wrapper {
  width: 100%;
}
@media (max-width: 599px) {
  .login-container { align-items: flex-start; }
}
</style>
